import { useMutation } from "@apollo/client/react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { config } from "~/config";
import {
  ActivateBatchEvidencesJobDocument,
  type ActivateBatchEvidencesJobMutation,
  type ActivateBatchEvidencesJobMutationVariables,
  DeactivateBatchEvidencesJobDocument,
  type DeactivateBatchEvidencesJobMutation,
  type DeactivateBatchEvidencesJobMutationVariables,
  GetBatchEvidencesJobsDocument,
  type GetBatchEvidencesJobsQueryVariables,
  UpdateBatchEvidencesJobRunDocument,
  type UpdateBatchEvidencesJobRunMutation,
  type UpdateBatchEvidencesJobRunMutationVariables,
} from "~/features/dataImportProjects/gqls";
import { useTenant } from "~/features/tenant/context";
import { useActionWithConfirm } from "~/lib/handleActionWithConfirm";

type UseBatchEvidencesJobActionsProps = {
  refetch: () => void;
  dataSourceId: string;
  canExecute?: () => boolean;
};

export const useBatchEvidencesJobActions = ({
  refetch,
  dataSourceId,
  canExecute,
}: UseBatchEvidencesJobActionsProps) => {
  const { t } = useTranslation();
  const actionWithConfirm = useActionWithConfirm();
  const { tenantId } = useTenant();
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }
  const listVariables: GetBatchEvidencesJobsQueryVariables = {
    tenantId,
    where: { dataSourceId: { _eq: dataSourceId } },
  };
  const [activateBatchEvidencesJob] = useMutation<
    ActivateBatchEvidencesJobMutation,
    ActivateBatchEvidencesJobMutationVariables
  >(ActivateBatchEvidencesJobDocument, {
    refetchQueries: [{ query: GetBatchEvidencesJobsDocument, variables: listVariables }],
  });
  const [deactivateBatchEvidencesJob] = useMutation<
    DeactivateBatchEvidencesJobMutation,
    DeactivateBatchEvidencesJobMutationVariables
  >(DeactivateBatchEvidencesJobDocument, {
    refetchQueries: [{ query: GetBatchEvidencesJobsDocument, variables: listVariables }],
  });
  const [updateJobRun] = useMutation<UpdateBatchEvidencesJobRunMutation, UpdateBatchEvidencesJobRunMutationVariables>(
    UpdateBatchEvidencesJobRunDocument,
  );
  const onActivateClick = useCallback(
    (beJob: { id: string; name: string }) => {
      actionWithConfirm({
        confirmMessage: t("ui.activateX", { x: beJob.name }),
        successMessage: t("feature.common.activatedSuccessfully", { model: "batchEvidencesJob" }),
        action: async () => {
          await activateBatchEvidencesJob({
            variables: { input: { tenantId, id: beJob.id } },
          });
        },
        canExecute,
      });
    },
    [actionWithConfirm, t, activateBatchEvidencesJob, tenantId, canExecute],
  );

  const onDeactivateClick = useCallback(
    (beJob: { id: string; name: string }) => {
      actionWithConfirm({
        confirmMessage: t("ui.deactivateX", { x: beJob.name }),
        successMessage: t("feature.common.deactivatedSuccessfully", { model: "batchEvidencesJob" }),
        action: async () => {
          await deactivateBatchEvidencesJob({
            variables: { input: { tenantId, id: beJob.id } },
          });
        },
        canExecute,
      });
    },
    [actionWithConfirm, t, deactivateBatchEvidencesJob, tenantId, canExecute],
  );

  const markJobRunAsFailed = useCallback(
    ({ jobRunId }: { jobRunId: string }) => {
      actionWithConfirm({
        confirmMessage: t("feature.batchEvidencesJobs.markJobRunAsFailed"),
        successMessage: t("feature.common.updatedSuccessfully", { model: "batchEvidencesJobRun" }),
        action: async () => {
          await updateJobRun({
            variables: { input: { tenantId, id: jobRunId, status: "failed" } },
          });
          refetch();
        },
        canExecute,
      });
    },
    [actionWithConfirm, t, updateJobRun, refetch, tenantId, canExecute],
  );

  return { onActivateClick, onDeactivateClick, markJobRunAsFailed };
};
